import type { IndexUiState, UiState } from 'instantsearch.js';
import { history } from 'instantsearch.js/es/lib/routers';
import type { RouterProps } from 'instantsearch.js/es/middlewares';
import singletonRouter from 'next/router';
import type { StoreLocale } from '@/root/constants';
import { DEFAULT_LOCALE } from '@/root/constants';
import type { StoreFilterSetting } from 'components/globalContentContext/GlobalContentContext';
import type { AlgoliaIndex, AlgoliaIndexLabel } from 'utils/algoliaConstants';
import {
  algoliaIndexToIndexLabel,
  algoliaProductIndex,
} from 'utils/algoliaConstants';

const routeMapping = [] as StoreFilterSetting[];

/** Set up constants. */
let defaultIndex: AlgoliaIndex = algoliaProductIndex(DEFAULT_LOCALE);
let searchMainIndex: AlgoliaIndex = algoliaProductIndex(DEFAULT_LOCALE);
let urlIndexParamValue: AlgoliaIndexLabel | undefined;
let indexPrefix = `${defaultIndex}_`;
const urlValueSeparator = '--';

export interface RouteState {
  [key: string]: string | number | undefined;
  query?: string;
  page?: number;
  sortBy?: string;
}

type RoutingType = 'collection' | 'search';

type AlgoliaRoutingConfig = {
  type?: RoutingType;
  filterSettings?: StoreFilterSetting[];
  index?: AlgoliaIndex;
  searchMainIndex?: AlgoliaIndex;
};

/**
 * Create an Algolia routing object for custom URL routing
 * @param options - RoutingConfig
 * @param locale - StoreLocale - used for setting the correct Algolia index
 */
export const algoliaRouting = (
  options: AlgoliaRoutingConfig = {
    type: 'collection',
  },
  locale: StoreLocale = 'en-US'
): RouterProps<UiState, RouteState> => {
  const { type, filterSettings } = options;

  /**
   * If `filterSettings` is set, push each filter setting to the `routeMapping`
   * array.
   */
  if (filterSettings) {
    routeMapping.length = 0;
    routeMapping.push(...filterSettings);
  }

  /**
   * Update index names based on the passed index or update the product index
   * with the provided locale.
   */
  defaultIndex = options?.index ?? algoliaProductIndex(locale);
  searchMainIndex = options?.searchMainIndex ?? algoliaProductIndex(locale);
  urlIndexParamValue = algoliaIndexToIndexLabel(locale, defaultIndex);
  /** Index prefix needs re-assigning as we've updated `defaultIndex`'s value */
  indexPrefix = `${defaultIndex}_`;

  const stateMapping = {
    stateToRoute,
    routeToState,
  };

  /**
   * If type is `search` use search specific state to route and route to state
   * logic.
   */
  if (type === 'search') {
    stateMapping.stateToRoute = stateToRouteSearch;
    stateMapping.routeToState = routeToStateSearch;
  }

  /** Store the initial pathname when routing is initialised
   * - Allows us to check if we have changed routes or not.
   * - Checks if `window` is undefined to handle server rendering - the Next
   *   Router can only be accessed on client and not server.
   */
  const initialPathname =
    typeof window === 'undefined' ? '' : singletonRouter.pathname;

  return {
    router: history({
      /**
       * Override the Algolia `history` router `push` to use the Next Router
       * instead.
       * - Only pushes with the Next Router if the pathname is the same.
       */
      push: (newUrl) => {
        let url = newUrl;

        /**
         * Locale edge case handler for using Algolia Routing on the root path.
         * - Pulled from the Algolia NextJS router code.
         * - We need to do this because there's an error when using i18n on the root path
         *   it says for example `pages/fr.js` doesn't exist
         */
        if (singletonRouter.locale) {
          url = url.replace('/'.concat(singletonRouter.locale), '');
        }

        const isSamePath = initialPathname === singletonRouter.pathname;

        if (!isSamePath) return;

        singletonRouter.push(url, undefined, {
          shallow: true,
        });
      },
      writeDelay: 0,
    }),
    stateMapping,
  };
};

/**
 * Convert UI state into router state
 * @param uiState - Algolia UI state object
 * @returns router object.
 */
const stateToRoute = (uiState: UiState): RouteState => {
  const indexUiState = uiState[defaultIndex] || {};

  return buildRoute(indexUiState, 'collection');
};

/**
 * Convert route state into UI state
 * @param routeState - Custom route state object
 * @returns Algolia state object
 */
const routeToState = (routeState: RouteState): UiState => {
  return {
    [defaultIndex]: buildState(routeState),
  };
};

/**
 * Convert UI state into router state for search page.
 * - Handles search case where the `query` parameter is on the default index, but
 *   the rest of the state is on the search `results_` prefixed index.
 * @param uiState - Algolia UI state object
 * @returns router object.
 */
const stateToRouteSearch = (uiState: UiState): RouteState => {
  const searchIndexUiStateKey = `results_${defaultIndex}`;
  let searchIndexUiState = {} as IndexUiState;

  if (uiState[searchIndexUiStateKey]) {
    searchIndexUiState = { ...uiState[searchIndexUiStateKey] };
  }

  if (uiState[searchMainIndex]?.query) {
    searchIndexUiState.query = uiState[searchMainIndex].query;
  }

  return buildRoute(searchIndexUiState, 'search');
};

/**
 * Convert route state into UI state for search page.
 * - Handles search case where the `query` parameter is on the default index, but
 *   the rest of the state is on the search `results_` prefixed index.
 * @param routeState - Custom route state object
 * @returns Algolia state object
 */
const routeToStateSearch = (routeState: RouteState): UiState => {
  const searchIndexUiStateKey = `results_${defaultIndex}`;
  const stateObject = buildState(routeState);
  const { query, ...searchProductUiState } = stateObject;

  return {
    [searchMainIndex]: {
      query,
    },
    [searchIndexUiStateKey]: searchProductUiState,
  };
};

/**
 * Reformat the UI State into the route state shape.
 * - Passes page and query without modification
 * - Formats the refinement list values
 * - Formats the sortBy state to remove the index prefix
 * @param indexUiState - UI State for the product index
 * @returns route state object
 */
const buildRoute = (indexUiState: IndexUiState, routingType?: RoutingType) => {
  const routeObject: RouteState = {
    page: indexUiState.page,
  };

  if (indexUiState.query) {
    routeObject.query = indexUiState.query;
  }

  if (indexUiState.refinementList) {
    Object.entries(indexUiState.refinementList).forEach(([key, value]) => {
      const routeKey = getRouteKey(key);
      const routeValues = formatValue(value);
      routeObject[routeKey] = routeValues;
    });
  }

  if (indexUiState.sortBy) {
    const cleanedIndex = indexUiState.sortBy.replace(indexPrefix, '');
    routeObject.sortBy = cleanedIndex;
  }

  /**
   * When on the search page, we additionally include a `view` parameter that is
   * set to an Algolia Index Label - e.g. `view=products`
   * - This allows us to link to a specific index tab on the search page.
   */
  if (routingType === 'search' && urlIndexParamValue) {
    routeObject.view = urlIndexParamValue;
  }

  /**
   * Append window search params to the route object to ensure additional
   * non-Algolia query parameters are preserved
   * - Creates a search params object from the `location.search`
   * - Loops through the search params, and if the search param has a key and
   *   value, and isn't already processed in the `routeObject`, adds it to the
   *   route object
   */
  const paramsString = location.search;
  const searchParams = new URLSearchParams(paramsString);

  for (const searchParam of searchParams) {
    const [key, value] = searchParam;
    if (
      key &&
      value &&
      !routeObject[key] &&
      !isAlgoliaSearchParam(key, routingType)
    ) {
      routeObject[key] = value;
    }
  }

  return routeObject;
};

/**
 * Converts route state into UI state shope.
 * - Passes query and page with no transformations
 * - Re-adds prefix to sortBy value
 * - Rebuilds refinementList object
 * @param routeState - route state
 * @returns UI State object
 */
const buildState = (routeState: RouteState): IndexUiState => {
  const stateObject = {} as IndexUiState;

  const { query, page, sortBy, ...rest } = routeState;

  if (query) {
    stateObject.query = query;
  }

  if (page) {
    stateObject.page = page;
  }

  if (sortBy) {
    const realIndex = `${indexPrefix}${sortBy}`;
    stateObject.sortBy = realIndex;
  }

  Object.entries(rest).forEach(([key, value]) => {
    if (!key || !value) {
      return;
    }

    const stateKey = getStateKey(key);
    const stateValue = getStateValue(value);

    /**
     * Check the stateKey is valid.
     */
    if (!isStateKeyValid(stateKey)) {
      return;
    }

    if (!stateValue) {
      return;
    }

    if (stateObject.refinementList) {
      stateObject.refinementList = {
        ...stateObject.refinementList,
        [stateKey]: stateValue,
      };

      return;
    }

    stateObject.refinementList = {
      [stateKey]: stateValue,
    };
  });

  return stateObject;
};

/**
 * Convert a url parameter value back to its UI state facet name.
 * @param key - refinement search parameter name
 * @returns refinement name for the UI State
 */
const getStateKey = (key: string) => {
  const matchedMapping = routeMapping.find((mapping) => {
    return key === mapping.searchParameter;
  });

  return matchedMapping ? matchedMapping.attribute : key;
};

/**
 * Convert route state value back to UI State type.
 * - Handles string of UI state back to an array type.
 * @param value - refinement search parameter name
 * @returns refinement name for the UI State
 */
const getStateValue = (value: RouteState['key']) => {
  const validValueTypes = ['string', 'number'];

  if (!value || !validValueTypes.includes(typeof value)) {
    return;
  }

  if (typeof value === 'number') {
    return [value.toString()];
  }

  return value.split(urlValueSeparator);
};

/**
 * Convert an facet name from the UI state into a user-friendly string.
 * - Checks the matched mapping values to match the key to the custom string.
 * - Falls back to to the key if there is no match.
 * @param key - refinement search parameter name
 * @returns refinement name for the UI State
 */
const getRouteKey = (key: string) => {
  const matchedMapping = routeMapping.find((mapping) => {
    return key === mapping.attribute;
  });

  return matchedMapping ? matchedMapping.searchParameter : key;
};

/**
 * Convert a value to use in the URL route state.
 * - If the value is an array, it is joined with the `urlValueSeparator`.
 * - Otherwise value is passed straight through.
 * @param value - value for the refinement
 * @returns value string for the URL.
 */
const formatValue = (value: string[] | string | number) => {
  if (Array.isArray(value)) {
    return value.join(urlValueSeparator);
  }

  return value;
};

/**
 * Check if the parameter from the URL is a valid attribute.
 * @param attribute - attribute name from the route object
 */
const isStateKeyValid = (attribute: string) => {
  return routeMapping.some((routeMap) => {
    return routeMap.attribute === attribute;
  });
};
/**
 * Check if a search parameter string is used for Algolia or not.
 * @param key - url search param key to check
 * @param routingType - whether the routing is for search or collection
 */
const isAlgoliaSearchParam = (key: string, routingType?: RoutingType) => {
  const baseKeys = ['query', 'page', 'sortBy'];
  if (routingType === 'search') {
    baseKeys.push('view');
  }

  const filtersParams = routeMapping.map((routeMap) => {
    return routeMap.searchParameter || routeMap.attribute;
  });

  return baseKeys.includes(key) || filtersParams.includes(key);
};
